import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { currency } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, remove, setQty, subtotal } = useCart();
  const nav = useNavigate();

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="cart-page">
      <div className="border-b divider-gold pb-8">
        <div className="overline text-emerald mb-3">Your bag</div>
        <h1 className="font-serif text-5xl md:text-6xl tracking-tight">Cart</h1>
      </div>

      {items.length === 0 ? (
        <div className="py-32 text-center">
          <div className="font-serif text-3xl text-charcoal/60">Your bag is empty.</div>
          <Link to="/shop" data-testid="cart-shop-link" className="mt-8 inline-flex items-center gap-2 overline text-emerald">Enter the atelier <ArrowRight className="w-4 h-4" /></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-16 mt-12">
          <div className="space-y-8">
            {items.map((i) => (
              <div key={i.product_id} className="flex items-start gap-6 pb-8 border-b divider-gold" data-testid={`cart-item-${i.product_id}`}>
                <Link to={`/product/${i.slug}`} className="w-28 h-32 bg-ivory shrink-0 img-hover">
                  <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${i.slug}`} className="font-serif text-xl tracking-tight hover:text-emerald">{i.name}</Link>
                  <div className="text-sm mt-1 text-charcoal/60">{currency(i.price)}</div>
                  <div className="mt-4 flex items-center gap-6">
                    <div className="flex items-center border divider-gold h-10">
                      <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-9 h-full"><Minus className="w-3 h-3 mx-auto" /></button>
                      <div className="w-8 text-center text-sm" data-testid={`cart-qty-${i.product_id}`}>{i.quantity}</div>
                      <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-9 h-full"><Plus className="w-3 h-3 mx-auto" /></button>
                    </div>
                    <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-xs text-charcoal/60 hover:text-emerald inline-flex items-center gap-2"><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                  </div>
                </div>
                <div className="font-serif text-lg">{currency(i.price * i.quantity)}</div>
              </div>
            ))}
          </div>

          <div className="bg-ivory/60 p-8 h-fit lg:sticky lg:top-28">
            <div className="overline text-emerald mb-4">Summary</div>
            <div className="flex justify-between py-2 text-sm"><span>Subtotal</span><span data-testid="cart-subtotal">{currency(subtotal)}</span></div>
            <div className="flex justify-between py-2 text-sm text-charcoal/60"><span>Shipping</span><span>{subtotal >= 2000 ? "Complimentary" : currency(99)}</span></div>
            <div className="flex justify-between py-2 text-sm text-charcoal/60"><span>Estimated tax</span><span>Calculated at checkout</span></div>
            <div className="border-t divider-gold mt-3 pt-4 flex justify-between items-baseline">
              <span className="overline">Total</span>
              <span className="font-serif text-2xl" data-testid="cart-total">{currency(subtotal + (subtotal >= 2000 ? 0 : 99))}</span>
            </div>
            <Button data-testid="checkout-btn" onClick={() => nav("/checkout")} className="mt-6 w-full rounded-none bg-emerald hover:bg-emerald/90 text-ivory h-12 tracking-[0.15em] uppercase text-xs">Checkout →</Button>
            <Link to="/shop" className="mt-4 block text-center overline text-charcoal/60 hover:text-emerald">Continue shopping</Link>
          </div>
        </div>
      )}
    </div>
  );
}
